import { useCallback, useEffect, useRef, useState } from "react";

type Props = {
  facing: "user" | "environment";
  onCapture: (file: File) => void;
  onClose: () => void;
};

// Живая камера через getUserMedia (в отличие от <input capture>, которую webview
// Telegram игнорирует и открывает галерею). Кадр снимается в canvas → JPEG File.
export function CameraCapture({ facing, onCapture, onClose }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error("unsupported");
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: facing } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => undefined);
        }
        setReady(true);
      } catch {
        if (!cancelled) setError("Не удалось открыть камеру. Разрешите доступ к камере или загрузите фото из галереи.");
      }
    })();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [facing]);

  const snap = useCallback(() => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (blob) onCapture(new File([blob], `photo-${facing}.jpg`, { type: "image/jpeg" }));
      },
      "image/jpeg",
      0.92,
    );
  }, [facing, onCapture]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black">
      {error ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6 text-center">
          <div className="text-[15px] text-white">{error}</div>
          <button onClick={onClose} className="rounded-xl bg-white/15 px-5 py-2.5 text-[15px] text-white">
            Закрыть
          </button>
        </div>
      ) : (
        <>
          <video
            ref={videoRef}
            playsInline
            muted
            autoPlay
            className={`min-h-0 flex-1 object-cover ${facing === "user" ? "-scale-x-100" : ""}`}
          />
          <div className="flex items-center justify-between bg-black px-8 py-6">
            <button onClick={onClose} className="text-[15px] text-white/80">
              Отмена
            </button>
            <button
              onClick={snap}
              disabled={!ready}
              aria-label="Снять кадр"
              className="h-16 w-16 rounded-full border-4 border-white bg-white/90 active:scale-95 disabled:opacity-40"
            />
            <span className="w-12" />
          </div>
        </>
      )}
    </div>
  );
}
